import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Plus, User as UserIcon, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { useResponsive } from '../../hooks/useResponsive';
import { useDebounce } from '../../hooks/useDebounce';
import { filterAndSortBySearch } from '../../utils/searchScore';
import { useContacts } from '../../../modules/master_data/contacts/hooks/useContacts';
import { ContactQuickCreateDialog } from '../../../modules/master_data/contacts/components/ContactQuickCreateDialog';
import type { Contact } from '../../../modules/master_data/contacts/types';
import { getContactDisplayName } from '../../../modules/master_data/contacts/utils/contactName';

export type ContactGroup = 'deal' | 'company' | 'other';

interface MultiContactPickerProps {
  value: string[];
  onChange: (contactIds: string[], allContacts: Contact[]) => void;
  /**
   * Contacts already on the deal (group 1). Passing it — even as an empty array —
   * marks this as a deal context and unlocks the deal-shaped copy. Non-deal
   * callers (e.g. newsletter subscribers) must omit it entirely.
   */
  dealContacts?: Contact[];
  /** When provided, contacts on this company are surfaced as group 2. */
  organizationId?: string | null;
  /** Optional name shown in the "From {company}" group heading. */
  companyName?: string | null;
  label?: string;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
  /**
   * When true, contacts without an email address are greyed out and cannot be
   * selected (e.g. newsletter subscribers, who can't receive issues without an
   * email). Defaults to false — most callers (deal activities, etc.) associate
   * contacts regardless of email.
   */
  requireEmail?: boolean;
  /** Rendered below the chips (e.g., an "also link to deal" checkbox). */
  belowChips?: ReactNode;
  /** Enable "+ Create '{query}'" — opens ContactQuickCreateDialog pre-filled with the search text. When organizationId is set, it's pre-selected on the new contact. */
  allowQuickCreate?: boolean;
  /** Pre-selected relationships for the quick-create contact (e.g., ['lead', 'prospect']). */
  quickCreateRelationships?: string[];
  /** Overrides the hint shown when the caller has no contacts to pre-populate the list. */
  emptyHint?: string;
  /** Overrides the heading of the caller-supplied first group (defaults to the Deal group heading). */
  primaryGroupLabel?: string;
}

function contactLabel(c: Contact): string {
  return getContactDisplayName(c) || c.email || '—';
}

/** Searchable text for a contact — name, email and title, as before. */
function contactSearchText(c: Contact): string {
  return [c.first_name, c.last_name, c.email, c.title].filter(Boolean).join(' ');
}

export function MultiContactPicker({
  value,
  onChange,
  dealContacts: dealContactsProp,
  organizationId,
  companyName,
  label,
  placeholder,
  helperText,
  disabled,
  requireEmail = false,
  belowChips,
  allowQuickCreate = false,
  quickCreateRelationships,
  emptyHint,
  primaryGroupLabel,
}: MultiContactPickerProps) {
  const { t } = useTranslation('common');
  // Omitting the prop (rather than passing []) is what marks a non-deal caller.
  const isDealContext = dealContactsProp !== undefined;
  const dealContacts = useMemo(() => dealContactsProp ?? [], [dealContactsProp]);
  const { isMobile } = useResponsive();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [locallyCreated, setLocallyCreated] = useState<Contact[]>([]);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const debouncedSearch = useDebounce(search, 300);
  const resolvedPlaceholder = placeholder ?? t('pickers.selectContacts');

  const companyContactsQuery = useContacts(
    organizationId ? { organization_id: organizationId, limit: 100 } : undefined,
    { enabled: open && !!organizationId }
  );
  const otherContactsQuery = useContacts(
    { search: debouncedSearch || undefined, limit: 25 },
    { enabled: open }
  );

  // Build a unified map of all known contacts (deal + company + other + previously selected).
  const knownContacts = useMemo(() => {
    const map = new Map<string, Contact>();
    for (const c of dealContacts) map.set(c.id, c);
    for (const c of companyContactsQuery.data?.data || []) {
      if (!map.has(c.id)) map.set(c.id, c);
    }
    for (const c of otherContactsQuery.data?.data || []) {
      if (!map.has(c.id)) map.set(c.id, c);
    }
    for (const c of locallyCreated) {
      if (!map.has(c.id)) map.set(c.id, c);
    }
    return map;
  }, [dealContacts, companyContactsQuery.data, otherContactsQuery.data, locallyCreated]);

  // Every selected id gets a row, resolved or not. `knownContacts` only ever
  // holds what the two queries fetched (the "other" arm is `limit: 25`), so a
  // caller HANDED ids — an existing support routing rule arrives as ids and
  // nothing else — would otherwise see the trigger read "0 selected" and no chips
  // at all, while `value` still carries those ids and still saves them. That is
  // the control lying about what it is about to submit, and it also leaves the
  // selection unremovable. An unresolved id names itself instead.
  const selectedRows = useMemo(
    () =>
      value.map((id) => {
        const contact = knownContacts.get(id);
        return { id, label: contact ? contactLabel(contact) : id.slice(0, 8) };
      }),
    [value, knownContacts]
  );

  const dealGroup = useMemo(
    () => filterAndSortBySearch(dealContacts, debouncedSearch, contactSearchText),
    [dealContacts, debouncedSearch]
  );

  const dealIds = useMemo(() => new Set(dealContacts.map((c) => c.id)), [dealContacts]);

  const companyGroup = useMemo(() => {
    if (!organizationId) return [];
    const all = companyContactsQuery.data?.data || [];
    return filterAndSortBySearch(
      all.filter((c) => !dealIds.has(c.id)),
      debouncedSearch,
      contactSearchText
    );
  }, [organizationId, companyContactsQuery.data, dealIds, debouncedSearch]);

  const organizationIds = useMemo(() => new Set(companyGroup.map((c) => c.id)), [companyGroup]);

  const otherGroup = useMemo(() => {
    const all = otherContactsQuery.data?.data || [];
    return all.filter((c) => !dealIds.has(c.id) && !organizationIds.has(c.id));
  }, [otherContactsQuery.data, dealIds, organizationIds]);

  const isLoading = companyContactsQuery.isLoading || otherContactsQuery.isLoading;
  const allEmpty = dealGroup.length === 0 && companyGroup.length === 0 && otherGroup.length === 0;
  const showEmptyHint = !debouncedSearch && dealContacts.length === 0 && (companyContactsQuery.data?.data?.length ?? 0) === 0;

  const trimmedQuery = search.trim();
  const showQuickCreate = allowQuickCreate && trimmedQuery.length > 0;

  const quickCreateNameParts = useMemo(() => {
    const parts = trimmedQuery.split(/\s+/);
    return {
      first_name: parts[0] || '',
      last_name: parts.slice(1).join(' '),
    };
  }, [trimmedQuery]);

  const handleOpenQuickCreate = () => {
    setOpen(false);
    setQuickCreateOpen(true);
  };

  const handleQuickCreated = (created: Contact) => {
    setLocallyCreated((prev) => [...prev, created]);
    const nextIds = [...value, created.id];
    const nextContacts = [
      ...(value.map((id) => knownContacts.get(id)).filter(Boolean) as Contact[]),
      created,
    ];
    onChange(nextIds, nextContacts);
    setSearch('');
  };

  const handleSelect = (contact: Contact) => {
    if (requireEmail && !contact.email) return;
    const exists = value.includes(contact.id);
    const nextIds = exists ? value.filter((id) => id !== contact.id) : [...value, contact.id];
    const nextContacts = nextIds
      .map((id) => knownContacts.get(id))
      .filter(Boolean) as Contact[];
    onChange(nextIds, nextContacts);
    setSearch('');
  };

  const handleRemove = (id: string) => {
    const nextIds = value.filter((v) => v !== id);
    const nextContacts = nextIds
      .map((vid) => knownContacts.get(vid))
      .filter(Boolean) as Contact[];
    onChange(nextIds, nextContacts);
  };

  const renderItem = (contact: Contact, group: ContactGroup) => {
    const isSelected = value.includes(contact.id);
    const hasNoEmail = requireEmail && !contact.email;
    return (
      <CommandItem
        key={`${group}-${contact.id}`}
        value={`${group}-${contact.id}-${contactLabel(contact)} ${contact.email || ''}`}
        onSelect={() => handleSelect(contact)}
        disabled={hasNoEmail}
      >
        <Check
          className={cn('me-2 h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
        />
        <UserIcon className="me-2 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <div className="truncate text-sm">{contactLabel(contact)}</div>
          <div className="truncate text-xs text-muted-foreground">
            {hasNoEmail
              ? t('pickers.contactNoEmail', 'No email address')
              : contact.title || contact.email}
          </div>
        </div>
      </CommandItem>
    );
  };

  return (
    <div className="w-full">
      {label && <Label className="mb-1.5 block">{label}</Label>}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'w-full justify-between font-normal',
              !selectedRows.length && 'text-muted-foreground'
            )}
          >
            <span className="truncate">
              {selectedRows.length > 0
                ? t('pickers.contactsSelected', '{{count}} selected', { count: selectedRows.length })
                : resolvedPlaceholder}
            </span>
            <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn('p-0', isMobile ? 'w-[calc(100vw-2rem)]' : 'w-[320px]')}
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t('pickers.searchContacts', 'Search contacts...')}
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {showEmptyHint && (
                <div className="px-3 py-2 text-xs text-muted-foreground border-b">
                  {emptyHint ??
                    (isDealContext
                      ? t('pickers.contactsEmptyHint', 'No contacts linked to this deal yet — pick from your tenant contacts below.')
                      : t('pickers.contactsEmptyHintGeneric', 'Search your contacts above.'))}
                </div>
              )}

              {allEmpty && !isLoading && !showQuickCreate && (
                <CommandEmpty>{t('app.noResults', 'No results found')}</CommandEmpty>
              )}

              {dealGroup.length > 0 && (
                <CommandGroup heading={primaryGroupLabel ?? t('pickers.dealContactsGroup', 'Deal {{contacts}}')}>
                  {dealGroup.map((c) => renderItem(c, 'deal'))}
                </CommandGroup>
              )}

              {companyGroup.length > 0 && (
                <CommandGroup
                  heading={
                    companyName
                      ? t('pickers.fromCompanyNamedGroup', 'From {{name}}', { name: companyName })
                      : t('pickers.fromOrganizationGroup', 'From {{Organization}}')
                  }
                >
                  {companyGroup.map((c) => renderItem(c, 'company'))}
                </CommandGroup>
              )}

              {otherGroup.length > 0 && (
                <CommandGroup
                  heading={
                    // "Other" only reads sensibly when a group precedes it.
                    dealGroup.length > 0 || companyGroup.length > 0
                      ? t('pickers.otherContactsGroup', 'Other {{contacts}}')
                      : t('pickers.allContactsGroup', '{{Contacts}}')
                  }
                >
                  {otherGroup.map((c) => renderItem(c, 'other'))}
                </CommandGroup>
              )}

              {showQuickCreate && (
                <CommandGroup>
                  <CommandItem
                    value={`__create__${trimmedQuery}`}
                    onSelect={handleOpenQuickCreate}
                    className="text-primary"
                  >
                    <Plus className="me-2 h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {t('pickers.createContactWithName', 'Create "{{name}}"', { name: trimmedQuery })}
                    </span>
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedRows.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedRows.map((c) => (
            <Badge
              key={c.id}
              variant="outline"
              className="gap-1 pe-1 py-0.5 font-normal bg-muted/50"
            >
              <span className="truncate max-w-[160px]">{c.label}</span>
              <span
                role="button"
                tabIndex={0}
                aria-label={t('app.remove', 'Remove')}
                className="ms-0.5 rounded-full p-0.5 hover:bg-muted cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(c.id);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRemove(c.id);
                  }
                }}
              >
                <X className="h-3 w-3" />
              </span>
            </Badge>
          ))}
        </div>
      )}

      {belowChips && selectedRows.length > 0 && (
        <div className="mt-2">{belowChips}</div>
      )}

      {helperText && <p className="text-xs mt-1 text-muted-foreground">{helperText}</p>}

      {allowQuickCreate && (
        <ContactQuickCreateDialog
          open={quickCreateOpen}
          onClose={() => setQuickCreateOpen(false)}
          defaultFirstName={quickCreateNameParts.first_name}
          defaultLastName={quickCreateNameParts.last_name}
          defaultCompanyId={organizationId || undefined}
          defaultRelationships={quickCreateRelationships}
          skipNavigation
          onCreated={handleQuickCreated}
        />
      )}
    </div>
  );
}

/** Helper: classify which group a contact belongs to relative to a deal. */
export function classifyContact(
  contactId: string,
  dealContactIds: Set<string>,
  companyContactIds: Set<string>
): ContactGroup {
  if (dealContactIds.has(contactId)) return 'deal';
  if (companyContactIds.has(contactId)) return 'company';
  return 'other';
}
